import { type ChatAppearance } from '@apps/web/components/phoebe_chat/chat_appearance';

type ChatInputAppearanceClasses = {
    readonly composer: string;
    readonly editorShell: string;
    readonly editor: string;
    readonly placeholder: string;
    readonly ghost: string;
    readonly actionRow: string;
    readonly leadingControl: string;
    readonly controlGroup: string;
    readonly leadingActions: string;
    readonly trailingActions: string;
    readonly voiceButton: string;
    readonly stopButton: string;
    readonly submitButton: (enabled: boolean) => string;
};

const DEFAULT_APPEARANCE: ChatInputAppearanceClasses = {
    composer: 'border-input bg-background rounded-2xl border p-3.5',
    editorShell: '',
    editor: 'min-h-12 sm:min-h-[2.75rem]',
    placeholder: '',
    ghost: '',
    actionRow: 'mt-2.5 flex flex-wrap justify-between',
    leadingControl: 'flex-1',
    controlGroup: 'flex shrink-0 items-center justify-end gap-2',
    leadingActions: 'contents',
    trailingActions: 'contents',
    voiceButton: '',
    stopButton: '',
    submitButton: (enabled) =>
        enabled
            ? 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer'
            : 'bg-primary/35 text-primary-foreground cursor-default',
};

export function getChatInputAppearanceClasses({
    appearance,
}: {
    readonly appearance: ChatAppearance;
}): ChatInputAppearanceClasses {
    if (appearance === 'default') {
        return DEFAULT_APPEARANCE;
    }

    return {
        composer:
            'grid min-h-[52px] grid-cols-[auto_minmax(0,1fr)_auto_auto] ' +
            'items-start rounded-full border border-[#DBE3E3] bg-white ' +
            'px-2 py-[7px] ' +
            '@max-[460px]/chat-pane:rounded-2xl ' +
            'shadow-[0_1px_2px_rgba(0,36,41,0.04),0_4px_14px_rgba(0,36,41,0.05)]',
        editorShell: 'col-start-2 row-start-1 min-w-0',
        editor: 'min-h-9 py-2 text-sm leading-5 sm:min-h-9',
        placeholder:
            'inset-y-0 truncate py-2 text-sm leading-5 ' +
            '@max-[460px]/chat-pane:hidden',
        ghost:
            'inset-y-0 py-2 text-sm leading-5 ' +
            '@max-[460px]/chat-pane:hidden',
        actionRow: 'contents',
        leadingControl:
            'pointer-events-auto col-start-3 row-start-1 ml-2 flex h-[38px] ' +
            'self-start items-center empty:hidden ' +
            '@max-[460px]/chat-pane:col-span-4 ' +
            '@max-[460px]/chat-pane:col-start-1 ' +
            '@max-[460px]/chat-pane:row-start-2 ' +
            '@max-[460px]/chat-pane:mt-1 ' +
            '@max-[460px]/chat-pane:ml-0 ' +
            '@max-[460px]/chat-pane:h-auto ' +
            '@max-[460px]/chat-pane:w-full ' +
            '[&_[data-slot=button]]:h-8 ' +
            '[&_[data-slot=button]]:rounded-full ' +
            '[&_[data-slot=button]]:bg-[#F0F5F5] ' +
            '[&_[data-slot=button]]:px-[11px] ' +
            '[&_[data-slot=button]]:text-xs ' +
            '[&_[data-slot=button]]:leading-4 ' +
            '[&_[data-slot=button]]:font-semibold ' +
            '[&_[data-slot=button]]:text-[#617578]',
        controlGroup: 'contents',
        leadingActions:
            'pointer-events-auto col-start-1 row-start-1 mr-2 flex h-[38px] ' +
            'self-start items-center gap-2 empty:hidden',
        trailingActions:
            'pointer-events-auto col-start-4 row-start-1 ml-2 flex h-[38px] ' +
            'self-start items-center justify-end gap-2',
        voiceButton: 'size-9',
        stopButton: 'size-[38px]',
        submitButton: (enabled) =>
            enabled
                ? 'size-[38px] cursor-pointer bg-[#5533EE] text-white hover:bg-[#4629C7]'
                : 'size-[38px] cursor-default bg-[#C9BFFA] text-white',
    };
}
